import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Grid, Tooltip } from '@material-ui/core';
import { ApplicationState } from '../../reducers';
import { ComponentManagementActionType } from '../../reducers/comp.actions';
import { saveEditorialContent } from '../../api/node';
import { useHotkeys } from 'react-hotkeys-hook';
import { UiActionType } from '../../reducers/ui.reducer';
import { getEditorialTypeLabel, EditorialStatus, User, UserRole, EditorialType, AnnoNode, AnnoNodeTypeName } from '../../domain/domain';
import { getEditorialContentByType } from './utils';
import PageLeaveConfirmationDialog from './../PageLeaveConfirmationDialog/PageLeaveConfirmationDialog';
import { isMacOs } from 'react-device-detect';

type Props = {
    hasActiveChange: boolean,
    handleActiveChange: (isChange: boolean) => void,
    setContentChanged: (isContentChanged: boolean) => void,
    contentChanged?: boolean,
    editingNodeId?: number,
}

const EdiorialContentToolbar: React.FC<Props> = ({ hasActiveChange, handleActiveChange, contentChanged, setContentChanged, editingNodeId }) => {
    const currentUser: User | undefined = useSelector((app: ApplicationState) => app.user.currentUser);
    const editorStates = useSelector((app: ApplicationState) => app.comp.editorialEditorStates);
    const editorNode = useSelector((app: ApplicationState) => app.comp.editorNode);
    const dispatch = useDispatch();
    const mode = useSelector((app: ApplicationState) => app.comp.editorialContentMode);
    const selectedEditorialType = useSelector((app: ApplicationState) => app.comp.selectedEditorialType);
    const selectedAnnoType = useSelector((app: ApplicationState) => app.comp.selectedAnnoType);
    const [originalState, setOriginalState] = useState<string>("");
    const [confirmCancelOpen, setConfirmCancelOpen] = useState<boolean>(false);
    const annoNodeHasChanges = useSelector((app: ApplicationState) => app.comp.annoChanges);
    const saveBtnTooltipText = isMacOs ? <span>&#x2318;+S</span> : "CTRL+S";

    const updateChildNodes = (nodes: AnnoNode[], newContent: string): AnnoNode[] => {
        return nodes.map(node => {
            if (node.annoId === editingNodeId) {
                return { ...node, workingCopy: newContent, subTreeHasUnapprovedChanges: true, nodeHasChanges: true };
            } else if (node.children) {
                return { ...node, children: updateChildNodes(node.children, newContent) };
            } else {
                return node;
            }
        });
    };

    useEffect(() => {
        if (selectedEditorialType === undefined) {
            return;
        }
        setOriginalState(editorStates.get(selectedEditorialType) !== undefined ? editorStates.get(selectedEditorialType)! : "");
        let result = editorNode?.editorialContent.find(a => a["editorialType"] === selectedEditorialType)
        if (result !== undefined && result.markedForDeletion === true) {
            dispatch({ type: ComponentManagementActionType.MARK_FOR_DELETION, mark: true });
        } else {
            dispatch({ type: ComponentManagementActionType.MARK_FOR_DELETION, mark: false });
        }

    }, [selectedEditorialType]);

    const handleCancelConfirmation = () => {
        setConfirmCancelOpen(false);
        handleActiveChange(false);
        setContentChanged(false);
        dispatch({ type: ComponentManagementActionType.DIFF_HAS_CHANGES, diffChanges: false });

        if (selectedEditorialType === undefined || originalState === undefined) {
            return;
        }

        dispatch({ type: ComponentManagementActionType.EDITORIAL_CONTENT_MODE_CHANGED, editorialContentMode: "view" });
        dispatch({ type: ComponentManagementActionType.EDITORIAL_CONTENT_HAS_CHANGES, hasChanges: false });
        let newStates = new Map<EditorialType, string>(editorStates.set(selectedEditorialType, originalState));
        dispatch({ type: ComponentManagementActionType.EDITORIAL_STATES_CHANGED, editorialStates: newStates });
    }

    const handleCloseConfirmation = () => {
        setConfirmCancelOpen(false);
    }

    useHotkeys('mod+s', (event) => {
        if (mode === 'edit' && contentChanged) {
            onSaveClicked();
            event.preventDefault();
        } else {
            event.preventDefault();
        }
    }, {
        enableOnFormTags: true,
        enableOnContentEditable: true
    });

    if (!currentUser || !editorNode) {
        return <></>
    }

    const checkUserPermissions = () => {
        const isUserHasPermissionsToEdit = currentUser.userRole === UserRole.PUBLICATIONS_TEAM || currentUser.userRole === UserRole.NON_PUBLICATIONS_TEAM || currentUser.userRole === UserRole.ADMINISTRATOR;
        const isUserHasPermissionsToApprove = currentUser.userRole === UserRole.PUBLICATIONS_TEAM || currentUser.userRole === UserRole.ADMINISTRATOR;

        if (!editorNode.belowEditorialLevel) {
            return {
                isUserHasPermissionsToEdit: false,
                isUserHasPermissionsToApprove: false
            };
        }

        return {
            isUserHasPermissionsToEdit,
            isUserHasPermissionsToApprove
        };
    }

    let { isUserHasPermissionsToEdit, isUserHasPermissionsToApprove } = checkUserPermissions();

    const shouldShowEditorialButtonsForNonPubTeam = (): boolean => {
        if (currentUser?.userRole === UserRole.NON_PUBLICATIONS_TEAM) {
            const restrictedEditorialTypes = [
                EditorialType.ANALYSIS,
                EditorialType.ANALYSIS_COMMENT,
                EditorialType.INDEX,
                EditorialType.FORM_INDEX,
            ];
            if (selectedEditorialType === undefined) {
                return false;
            }
            return !restrictedEditorialTypes.includes(selectedEditorialType);
        }
        return true;
    };

    const onCancelClicked = () => {
        const isNonAnnotationWithChanges = hasActiveChange && selectedEditorialType !== EditorialType.ANNOTATION;
        const isAnnotationWithNodeChanges = selectedEditorialType === EditorialType.ANNOTATION && annoNodeHasChanges;
        const shouldConfirmCancel = isNonAnnotationWithChanges || isAnnotationWithNodeChanges;
        if (shouldConfirmCancel) {
            setConfirmCancelOpen(true);
        } else {
            setConfirmCancelOpen(false);
            dispatch({
                type: ComponentManagementActionType.EDITORIAL_CONTENT_MODE_CHANGED,
                editorialContentMode: "view"
            });
        }
    };

    const onSaveClicked = () => {
        handleActiveChange(false);
        setContentChanged(false);
        if (selectedEditorialType) {
            document.getElementById("editorial-editor")?.focus();
            let editorState = editorStates.get(selectedEditorialType);
            let newContent = editorState!;
            let body: any;
            if (selectedEditorialType === EditorialType.ANNOTATION) {
                if (selectedAnnoType !== AnnoNodeTypeName.CONTENT) {
                    newContent = newContent.replace(/^<p>([\s\S]*?)<\/p>$/, '$1')
                }
                let annoNodes = editorNode?.annotations;
                if (annoNodes?.children) {
                    let resultNodes = updateChildNodes(annoNodes.children, newContent);
                    if (editingNodeId && annoNodes) {
                        annoNodes = { ...annoNodes, children: resultNodes }
                    }
                }
                body = annoNodes
            } else {
                body = { content: newContent }
            }
            saveEditorialContent(editorNode, selectedEditorialType, body).then(response => {
                dispatch({ type: ComponentManagementActionType.EDITOR_NODE_UPDATED, editorNode: response.data });
                dispatch({ type: ComponentManagementActionType.SUBTREE_CHANGED, nodeId: response.data.nodeId, hasChanges: true });
                dispatch({
                    type: ComponentManagementActionType.EDITORIAL_CONTENT_CHANGED,
                    nodeId: response.data.nodeId,
                    editorialContentHasChanges: true,
                })
                let message = getEditorialTypeLabel(selectedEditorialType) + " saved.";
                dispatch({ type: UiActionType.SNACKBAR_STATE_CHANGED, newState: { open: true, message: message, severity: "success" } });
                setOriginalState(editorStates.get(selectedEditorialType)!);
            }).catch(error => {
                console.error(error);
            });
        }
    }

    const onApproveClicked = () => {
        dispatch({ type: ComponentManagementActionType.APPROVE_EDITORIAL_CONTENT_DIALOG_OPEN, open: true });
    }

    const onRejectClicked = () => {
        dispatch({ type: ComponentManagementActionType.REJECT_EDITORIAL_CONTENT_DIALOG_OPEN, open: true });
    }

    const isApproveEnabled = () => {
        if (selectedEditorialType === EditorialType.ANNOTATION) {
            return editorNode.annotations?.subTreeHasUnapprovedChanges && isUserHasPermissionsToApprove;
        }
        return getEditorialContentByType(editorNode.editorialContent, selectedEditorialType!)?.status === EditorialStatus.NOT_APPROVED && isUserHasPermissionsToApprove;
    }

    const disableSaveECButton = () => {
        if (selectedEditorialType === EditorialType.ANNOTATION) {
            return !annoNodeHasChanges;
        }
        return !contentChanged;
    }

    let approveEnabled = isApproveEnabled();

    let isShowEditorialButtonsForNonPubTeam = shouldShowEditorialButtonsForNonPubTeam();

    return (
        <>
            <Grid container direction='row' spacing={1} style={{ paddingTop: 0, paddingBottom: 8 }}>
                {mode === "edit" && (
                    <Grid item>
                        <Button
                            variant='outlined'
                            color='secondary'
                            size='small'
                            onClick={onCancelClicked}
                        >
                            Close
                        </Button>
                    </Grid>
                )}
                {isUserHasPermissionsToEdit && isShowEditorialButtonsForNonPubTeam &&
                    <Grid item>
                        {mode === "edit" && (
                            <Tooltip title={saveBtnTooltipText} enterDelay={0} arrow placement="top">
                                <Button
                                    disabled={disableSaveECButton()}
                                    variant='outlined'
                                    color='primary'
                                    size='small'
                                    onClick={() => {
                                        onSaveClicked();
                                    }}
                                >
                                    Save EC
                                </Button>
                            </Tooltip>
                        )}
                        {mode === "view" && (
                            <Button
                                variant='outlined'
                                color='secondary'
                                size='small'
                                onClick={
                                    () => {
                                        dispatch({ type: ComponentManagementActionType.EDITORIAL_CONTENT_MODE_CHANGED, editorialContentMode: "edit" });
                                    }}
                            >
                                Edit EC
                            </Button>
                        )}
                    </Grid>
                }
                {mode !== 'edit' && isUserHasPermissionsToApprove &&
                    <>
                        {currentUser.userRole !== UserRole.PUBLICATIONS_TEAM &&
                            <Grid item>
                                <Button
                                    size='small'
                                    variant='outlined'
                                    color='secondary'
                                    onClick={onApproveClicked}
                                    disabled={!approveEnabled}
                                >
                                    Approve EC changes
                                </Button>
                            </Grid>
                        }
                        <Grid item>
                            <Button
                                size='small'
                                variant='outlined'
                                color='secondary'
                                onClick={onRejectClicked}
                                disabled={!approveEnabled}
                            >
                                Reject EC changes
                            </Button>
                        </Grid>
                    </>
                }
            </Grid>
            <PageLeaveConfirmationDialog
                open={confirmCancelOpen}
                text={"You have made a change. Are you sure you want to exit without saving your changes? All changes will be lost"}
                onCancel={handleCloseConfirmation}
                onConfirm={handleCancelConfirmation}
            />
        </>
    )
}

export default EdiorialContentToolbar;
